/**
 * 4.3AppStore - Main App Component
 * 
 * Class component for Chrome 68 compatibility
 * Loads app data from API and renders app store interface
 */

import React, { Component } from 'react';
import Papa from 'papaparse';
import Header from './components/Header';
import AppGrid from './components/AppGrid';
import Footer from './components/Footer';
import VotePage from './components/VotePage';
import ProfilePage from './components/ProfilePage';
import AdminPage from './components/AdminPage';
import LegacyLoginPanel from './components/LegacyLoginPanel';
import qrcode from 'qrcode-generator';
import { getApiBase, buildApiClient } from './services/apiClient';
import { filterAndSort } from './services/filters';
import { detectLegacyBrowser, startLegacyLoginFlow } from './services/legacyLogin';
import { loadJamScripts } from './services/jamLoader';

// API endpoint - can be overridden via environment variable
// Falls back to CSV for local development
var API_BASE = getApiBase();
var CSV_PATH = process.env.PUBLIC_URL + '/data/apps.csv';
var CSV_INDEX_PATH = process.env.PUBLIC_URL + '/data/apps.index.json';
var DISPLAY_PAGE_SIZE = 80;

/**
 * Main App Component
 */
class App extends Component {
    constructor(props) {
        super(props);
        
        this.state = {
            apps: [],
            filteredApps: [],
            isLoading: true,
            error: null,
            searchQuery: '',
            selectedCategories: [],  // Array for multiple categories
            selectedSource: 'all',
            selectedVendor: '',
            activeSorts: [],  // Array of { field: string, direction: 'asc'|'desc' }
            categories: [],
            sources: [],
            displayCount: DISPLAY_PAGE_SIZE,
            user: null,
            userVotes: [],
            voteLookup: {}, // package_name -> vote_type
            profileTab: 'up',
            isLegacyBrowser: false,
            legacyCode: null,
            legacyLoginUrl: '',
            legacyQrData: '',
            legacyLoginActive: false,
            voteFormInstalled: false,
            voteFormCrashed: false,
            voteFormComment: '',
            voteFormType: 'up',
            commentData: {}, // package_name -> { items, page, total, loading, error }
            activeCommentPackage: '',
            activeCommentApp: null,
            sessionChecked: false,
            randomSeed: this.loadRandomSeed(),
            apiPage: 1,
            apiHasMore: false,
            apiLoading: false,
            apiPageSize: DISPLAY_PAGE_SIZE,
            totalApps: 0,
            headerKey: 0
        };
        this.state.votePageApp = null;
        this.state.votePagePackage = '';
        // user votes pagination/infinite scroll
        this.state.userVotesPage = 1;
        this.state.userVotesPageSize = 100;
        this.state.userVotesHasMore = false;
        this.state.userVotesLoading = false;
        this.state.userVotesTotal = 0;
        // Base API URL (if set via REACT_APP_API_URL), otherwise same origin
        this.apiBase = (API_BASE || '').replace(/\/$/, '');
        this.apiClient = buildApiClient(this.apiBase);
        
        // Bind methods
        this.handleSearch = this.handleSearch.bind(this);
        this.handleCategoryChange = this.handleCategoryChange.bind(this);
        this.handleSourceChange = this.handleSourceChange.bind(this);
        this.handleSortChange = this.handleSortChange.bind(this);
        this.handleVendorClick = this.handleVendorClick.bind(this);
        this.loadMore = this.loadMore.bind(this);
        this.filterAndSortApps = this.filterAndSortApps.bind(this);
        this.goToProfile = this.goToProfile.bind(this);
        this.handleLogout = this.handleLogout.bind(this);
        this.initVoteFormFromQuery = this.initVoteFormFromQuery.bind(this);
        this.handleVoteSubmit = this.handleVoteSubmit.bind(this);
        this.handleVoteFieldChange = this.handleVoteFieldChange.bind(this);
        this._onScroll = this._onScroll.bind(this);
        this.openComments = this.openComments.bind(this);
        this.closeComments = this.closeComments.bind(this);
        this.loadCommentsPage = this.loadCommentsPage.bind(this);
        this.sanitizeComment = this.sanitizeComment.bind(this);
        this.isAdminPage = this.isAdminPage.bind(this);
        this.goToAdmin = this.goToAdmin.bind(this);
        this.getAuthHeaders = this.getAuthHeaders.bind(this);
        this.refreshRandomSeed = this.refreshRandomSeed.bind(this);
        this.loadMeta = this.loadMeta.bind(this);
        this.loadVoteApp = this.loadVoteApp.bind(this);
        this.syncSearchQueryToUrl = this.syncSearchQueryToUrl.bind(this);
        this._prevBodyOverflow = '';
        this._prevScrollTop = 0;
        this._pendingApiPage = null;
        this._jamLoadScheduled = false;
    }

    loadVoteApp(pkg) {
        var self = this;
        if (!pkg) return;
        // remember requested package to avoid duplicate loads
        this.setState({ votePagePackage: pkg });
        if (!this.apiBase) return; // only fetch from API when available
        fetch(this.api('/api/app?package=' + encodeURIComponent(pkg)))
            .then(function(res) {
                if (!res.ok) {
                    // Not found or server error: fail the vote page and redirect to main
                    alert('App not found');
                    window.location.href = '/';
                    throw new Error('App fetch failed');
                }
                return res.json();
            })
            .then(function(data) {
                if (!data || !data.app) {
                    alert('App not found');
                    window.location.href = '/';
                    return;
                }
                self.setState({ votePageApp: data.app });
            })
            .catch(function() {
                try { alert('Failed to load app'); } catch (e) {}
                try { window.location.href = '/'; } catch (e) {}
            });
    }

    loadRandomSeed() {
        // Always generate a fresh seed on each load (no reuse across reloads)
        var seed = Math.floor(Math.random() * 2147483647);
        try { sessionStorage.setItem('bb_random_seed', String(seed)); } catch (e) {}
        return seed;
    }

    refreshRandomSeed(callback, skipFilter) {
        var seed = Math.floor(Math.random() * 2147483647);
        try { sessionStorage.setItem('bb_random_seed', String(seed)); } catch (e) {}
        var self = this;
        this.setState({ randomSeed: seed }, function() {
            if (!skipFilter) {
                // Reload from API with new seed so server-side random ordering updates
                if (self.apiBase) {
                    self.loadFromAPI(1, true);
                } else {
                    self.filterAndSortApps();
                }
            }
            if (typeof callback === 'function') {
                callback(seed);
            }
        });
        return seed;
    }

    componentDidMount() {
        this.bootstrapSessionFromUrl();
        this.detectLegacyBrowser();
        // Preload search from session storage (used when navigating from vote/profile pages)
        try {
            var pending = sessionStorage.getItem('bb_pending_search') || '';
            if (pending) {
                this.state.searchQuery = pending;
                sessionStorage.removeItem('bb_pending_search');
            }
        } catch (e) {}

        // Preload search from URL for shareable links: /?q=term
        try {
            if (!this.state.searchQuery) {
                var qp = this.parseQuery();
                if (qp && qp.q) {
                    this.state.searchQuery = qp.q;
                }
            }
        } catch (e) {}

        // Keep the URL in sync when the initial query comes from session storage
        this.syncSearchQueryToUrl(this.state.searchQuery);

        this.loadMeta();
        this.fetchSession();
        this.loadData();
        if (typeof window !== 'undefined') window.addEventListener('scroll', this._onScroll);
        // Jam scripts are injected after initial app load (see componentDidUpdate).
        if (this.isVotePage()) {
            this.initVoteFormFromQuery();
            try {
                var qp = this.parseQuery();
                var pkg = qp.package || qp.pkg || '';
                if (pkg) {
                    // If we already have the app in state.apps (navigated from main), avoid extra fetch
                    var existing = (this.state.apps || []).find(function(a) { return a.package_name === pkg; });
                    if (existing) {
                        this.setState({ votePageApp: existing, votePagePackage: pkg });
                    } else {
                        this.loadVoteApp(pkg);
                    }
                }
            } catch (e) {}
        }
    }

    componentDidUpdate(prevProps, prevState) {
        // If user navigates to /vote with a different package, load that app
        try {
            if (this.isVotePage()) {
                var qpNow = this.parseQuery();
                var pkgNow = qpNow.package || qpNow.pkg || '';
                if (pkgNow && pkgNow !== (this.state.votePagePackage || '')) {
                    // Only fetch if not already available in state.apps
                    var existingNow = (this.state.apps || []).find(function(a) { return a.package_name === pkgNow; });
                    if (existingNow) {
                        this.setState({ votePageApp: existingNow, votePagePackage: pkgNow });
                    } else {
                        this.loadVoteApp(pkgNow);
                    }
                }
            }
        } catch (e) {}

        // After the initial app data has loaded, optionally inject Jam.dev scripts.
        try {
            if (!this._jamLoadScheduled && prevState && prevState.isLoading && !this.state.isLoading) {
                this._jamLoadScheduled = true;
                this.scheduleJamLoad();
            }
        } catch (e) {}
    }
    
    componentWillUnmount() {
        if (typeof window !== 'undefined') window.removeEventListener('scroll', this._onScroll);
    }

    scheduleJamLoad() {
        var self = this;
        try {
            if (typeof window === 'undefined') return;
            if (this.state && this.state.isLegacyBrowser) return;
            var run = function() {
                try { loadJamScripts(); } catch (e) {}
            };
            // Wait until the window has finished loading, then defer to idle time.
            var start = function() {
                try {
                    if (typeof window.requestIdleCallback === 'function') {
                        window.requestIdleCallback(run, { timeout: 3000 });
                    } else {
                        setTimeout(run, 1000);
                    }
                } catch (e) { try { setTimeout(run, 1000); } catch (e2) {} }
            };
            if (document && document.readyState === 'complete') {
                start();
            } else {
                window.addEventListener('load', start, { once: true });
            }
        } catch (e) {}
    }

    isProfilePage() {
        if (typeof window === 'undefined') return false;
        var path = window.location.pathname || '';
        return path === '/profile';
    }

    isVotePage() {
        if (typeof window === 'undefined') return false;
        var path = window.location.pathname || '';
        return path === '/vote';
    }

    isAdminPage() {
        if (typeof window === 'undefined') return false;
        var path = window.location.pathname || '';
        return path === '/admin';
    }

    goToProfile() {
        if (typeof window !== 'undefined') {
            window.location.href = '/profile';
        }
    }

    goToAdmin() {
        if (typeof window !== 'undefined') {
            window.location.href = '/admin';
        }
    }

    parseQuery() {
        if (typeof window === 'undefined') return {};
        try {
            var url = new URL(window.location.href);
            var out = {};
            url.searchParams.forEach(function(value, key) {
                out[key] = value;
            });
            return out;
        } catch (e) {
            var params = {};
            var q = window.location.search || '';
            if (q.indexOf('?') === 0) q = q.slice(1);
            var parts = q.split('&');
            for (var i = 0; i < parts.length; i++) {
                var kv = parts[i].split('=');
                if (kv.length >= 2) {
                    var key = kv.shift();
                    var value = kv.join('=');
                    params[decodeURIComponent(key)] = decodeURIComponent(value);
                }
            }
            return params;
        }
    }

    initVoteFormFromQuery() {
        var params = this.parseQuery();
        var type = params.type || 'up';
        var pkg = params.package || params.pkg || '';
        this.setState({
            voteFormType: (type === 'down') ? 'down' : 'up',
            voteFormInstalled: false,
            voteFormCrashed: false,
            voteFormComment: '',
            voteFormPackage: pkg
        });
    }

    handleLogout() {
        var self = this;
        fetch(this.api('/api/auth/logout'), { 
            method: 'POST', 
            credentials: 'include',
            headers: this.getAuthHeaders()
        })
            .then(function() { 
                try { localStorage.removeItem('bb_session'); } catch (e) {}
                self.setState({ user: null, userVotes: [], voteLookup: {} });
                window.location.href = '/';
            });
    }

    startLegacyLogin() {
        var self = this;
        if (typeof window !== 'undefined') {
            this._prevScrollTop = window.pageYOffset || document.documentElement.scrollTop || 0;
        }
        this.setState({ legacyCode: null, legacyLoginUrl: '', legacyQrData: '' });
        this.setState({ legacyLoginActive: true });
        startLegacyLoginFlow(
            this.api.bind(this),
            qrcode,
            function(started) {
                self.setState({
                    legacyCode: started.code,
                    legacyLoginUrl: started.loginUrl,
                    legacyQrData: started.qrDataUrl
                });
            },
            function() {
                self.fetchSession();
            }
        );
    }

    getAppMetaForPackage(pkg) {
        var apps = this.state.apps || [];
        for (var i = 0; i < apps.length; i++) {
            if (apps[i].package_name === pkg) {
                return apps[i];
            }
        }
        return null;
    }

    detectLegacyBrowser() {
        var ua = (typeof navigator !== 'undefined' && navigator.userAgent) ? navigator.userAgent : '';
        var isLegacy = detectLegacyBrowser(ua);
        this.setState({ isLegacyBrowser: isLegacy });
    }

    api(path) {
        return this.apiClient.api(path);
    }
    
    loadData() {
        var self = this;
        
        // Try API first, fall back to CSV for local dev
        if (this.apiBase) {
            this.loadFromAPI(1, true);
        } else {
            this.loadFromCSV();
        }
    }

    loadMeta() {
        var self = this;
        if (!this.apiBase) return;
        fetch(this.api('/api/categories'))
            .then(function(r){ return r.json(); })
            .then(function(data){
                var cats = (data && data.categories) ? data.categories.map(function(c){ return c.category; }) : [];
                self.setState({ categories: cats });
            })
            .catch(function(){});
        fetch(this.api('/api/sources'))
            .then(function(r){ return r.json(); })
            .then(function(data){
                var srcs = (data && data.sources) ? data.sources.map(function(s){ return s.source; }) : [];
                self.setState({ sources: srcs });
            })
            .catch(function(){});
    }
    
    loadFromAPI(page, reset) {
        var self = this;
        var params = [];
        var p = page || 1;
        params.push('page=' + p);
        params.push('page_size=' + (this.state.apiPageSize || DISPLAY_PAGE_SIZE));
        // Include deterministic seed for random ordering
        if (this.state.randomSeed) params.push('seed=' + encodeURIComponent(this.state.randomSeed));
        // Server supports multi-sort: send all activeSorts in precedence order.
        if (this.state.activeSorts && this.state.activeSorts.length > 0) {
            var sortFields = [];
            var sortDirs = [];
            for (var si = 0; si < this.state.activeSorts.length; si++) {
                var s = this.state.activeSorts[si];
                if (!s || !s.field) continue;
                sortFields.push(s.field);
                sortDirs.push(s.direction || '');
            }
            if (sortFields.length) {
                params.push('sort=' + encodeURIComponent(sortFields.join(',')));
                if (sortDirs.length) {
                    params.push('direction=' + encodeURIComponent(sortDirs.join(',')));
                }
            }
        } else {
            params.push('sort=' + encodeURIComponent('random'));
        }
        
        if (this.state.searchQuery) params.push('q=' + encodeURIComponent(this.state.searchQuery));
        if (this.state.selectedSource && this.state.selectedSource !== 'all') params.push('source=' + encodeURIComponent(this.state.selectedSource));
        if (this.state.selectedVendor) params.push('vendor=' + encodeURIComponent(this.state.selectedVendor));
        if (this.state.selectedCategories && this.state.selectedCategories.length > 0) {
            params.push('categories=' + encodeURIComponent(this.state.selectedCategories.join(',')));
        }
        var url = this.api('/api/apps') + '?' + params.join('&');
        if (reset) {
            this.setState({ apiLoading: true, apiPage: p, apiHasMore: false, totalApps: 0, apps: [], filteredApps: [] });
        } else {
            this.setState({ apiLoading: true });
        }
        fetch(url)
            .then(function(response) {
                if (!response.ok) {
                    throw new Error('API request failed');
                }
                return response.json();
            })
            .then(function(data) {
                self._pendingApiPage = null;
                var incoming = data.apps || [];
                var apps = reset ? incoming : (self.state.apps || []).concat(incoming);
                var total = data.total || apps.length;
                var hasMore = (apps.length < total);
                self.setState({
                    apps: apps,
                    filteredApps: apps,
                    isLoading: false,
                    apiPage: p,
                    apiHasMore: hasMore,
                    totalApps: total,
                    apiLoading: false,
                    displayCount: Math.min(apps.length, self.state.displayCount || DISPLAY_PAGE_SIZE)
                }, function() {
                    self.filterAndSortApps(true);
                });
            })
            .catch(function(error) {
                self._pendingApiPage = null;
                console.warn('API failed:', error);
                self.setState({ apiLoading: false, error: 'Failed to load apps from API' });
            });
    }
    
    loadFromCSV() {
        var self = this;
        
        // Load split CSVs (apps.index.json) or fallback to single apps.csv
            (async function() {
                try {
                    // Check for index file first
                    var idxRes = await fetch(CSV_INDEX_PATH);
                    if (idxRes.ok) {
                        var idx = await idxRes.json();
                        var parts = idx.parts || [];
                        var combinedText = '';
                        for (var i = 0; i < parts.length; i++) {
                            var p = parts[i];
                            var partUrl = process.env.PUBLIC_URL + '/data/' + p;
                            var partRes = await fetch(partUrl);
                            if (!partRes.ok) throw new Error('Failed to fetch ' + p);
                            var text = await partRes.text();
                            // Ensure we don't duplicate header rows: only keep header for first part
                            if (i === 0) {
                                combinedText += text.trim();
                            } else {
                                // Find first newline (end of header)
                                var newlineIndex = text.indexOf('\n');
                                if (newlineIndex >= 0) {
                                    combinedText += '\n' + text.slice(newlineIndex + 1).trim();
                                } else {
                                    combinedText += '\n' + text.trim();
                                }
                            }
                        }
                        self.parseCSV(combinedText, '');
                        return;
                    }
                } catch (e) {
                    // ignore and fallback
                }

                // Fallback to single CSV file
                try {
                    var res = await fetch(CSV_PATH);
                    if (!res.ok) throw new Error('Failed to fetch CSV');
                    var appsText = await res.text();
                    self.parseCSV(appsText, '');
                } catch (err) {
                    console.error('Failed to load CSV data:', err);
                    self.setState({ error: 'Failed to load app database. Please check your connection and try again.', isLoading: false });
                }
            })();
    }
    
    processApps(apiApps) {
        var self = this;
        
        var apps = apiApps.map(function(row, index) {
            // Infer vendor from package name if missing
            var vendor = row.vendor || '';
            if (!vendor || vendor === 'Unknown' || vendor === '') {
                var pkgParts = (row.package_name || '').split('.');
                if (pkgParts.length >= 2) {
                    var vendorPart = pkgParts[1] || '';
                    if (vendorPart && vendorPart.length > 2) {
                        vendor = vendorPart.charAt(0).toUpperCase() + vendorPart.slice(1);
                    }
                }
            }
            
            return {
                id: index,
                package_name: row.package_name || '',
                app_name: row.app_name || row.package_name || 'Unknown App',
                vendor: vendor,
                version: row.version || '',
                rating: parseFloat(row.rating) || 0,
                download_link: row.download_link || '',
                direct_apk_link: row.direct_apk_link || '',
                category: row.category || 'Other',
                tags: row.tags || '',
                file_size_mb: parseFloat(row.file_size_mb) || 0,
                permissions: row.permissions || '',
                last_updated: row.last_updated || '',
                source: row.source || 'unknown',
                min_api: parseInt(row.min_api) || 0,
                max_api: parseInt(row.max_api) || 0,
                description: row.description || '',
                icon_url: row.icon_url || '',
                comment_count: parseInt(row.comment_count) || 0,
                voteData: (row.upvotes !== undefined || row.downvotes !== undefined) ? {
                    upvotes: parseInt(row.upvotes) || 0,
                    downvotes: parseInt(row.downvotes) || 0
                } : null
            };
        });
        
        // Extract unique categories and sources
        var categoriesSet = {};
        var sourcesSet = {};
        
        apps.forEach(function(app) {
            if (app.category) categoriesSet[app.category] = true;
            if (app.source) sourcesSet[app.source] = true;
        });
        
        var categories = Object.keys(categoriesSet).sort();
        var sources = Object.keys(sourcesSet).sort();
        
        self.setState({
            apps: apps,
            filteredApps: apps,
            categories: categories,
            sources: sources,
            isLoading: false
        }, function() {
            self.filterAndSortApps();
        });
    }
    
    parseCSV(csvText, votesCSV) {
        var self = this;
        
        Papa.parse(csvText, {
            header: true,
            skipEmptyLines: true,
            complete: function(results) {
                // Reuse processApps logic
                self.processApps(results.data);
            },
            error: function(error) {
                self.setState({
                    error: 'Failed to parse CSV: ' + error.message,
                    isLoading: false
                });
            }
        });
    }
    
    handleSearch(query) {
        var self = this;
        if (!query) query = '';
        // For vote/profile pages, defer search to home via session storage
        if (query && (this.isVotePage() || this.isProfilePage())) {
            try { sessionStorage.setItem('bb_pending_search', query); } catch (e) {}
            window.location.href = '/';
            return;
        }
        // Main page behavior: immediate filter/sort
        this.syncSearchQueryToUrl(query);
        this.setState({ searchQuery: query, displayCount: DISPLAY_PAGE_SIZE }, function() {
            if (self.apiBase) {
                self.loadFromAPI(1, true);
            } else {
                self.filterAndSortApps();
            }
        });
    }
    
    getAuthHeaders() {
        return this.apiClient.getAuthHeaders();
    }

    bootstrapSessionFromUrl() {
        if (typeof window === 'undefined') return;
        var url = new URL(window.location.href);
        var token = url.searchParams.get('session_token');
        if (token) {
            try {
                localStorage.setItem('bb_session', token);
            } catch (e) {}
            // Remove token from URL to avoid leaking it
            url.searchParams.delete('session_token');
            var clean = url.toString();
            window.history.replaceState({}, document.title, clean);
        }
    }

    syncSearchQueryToUrl(query) {
        if (typeof window === 'undefined') return;
        if (this.isVotePage() || this.isProfilePage() || this.isAdminPage()) return;
        try {
            var url = new URL(window.location.href);
            if (query && query !== '') {
                url.searchParams.set('q', query);
            } else {
                url.searchParams.delete('q');
            }
            window.history.replaceState({}, document.title, url.toString());
        } catch (e) {}
    }
    
    fetchSession() {
        var self = this;
        fetch(this.api('/api/me'), { 
            credentials: 'include',
            headers: this.getAuthHeaders()
        })
            .then(function(res) { return res.json(); })
            .then(function(data) {
                if (data && data.user) {
                    try { localStorage.setItem('bb_user_id', String(data.user.user_id || data.user.id || '')); } catch (e) {}
                    self.setState({ user: data.user, legacyLoginActive: false, legacyCode: null, legacyLoginUrl: '', legacyQrData: '' }, function() {
                        if (typeof window !== 'undefined' && self._prevScrollTop) {
                            window.scrollTo(0, self._prevScrollTop);
                        }
                        self.loadUserVotes(1, true);
                    });
                    self._prevScrollTop = 0;
                } else {
                    try { localStorage.removeItem('bb_user_id'); } catch (e) {}
                    self.setState({ user: null, userVotes: [], voteLookup: {} });
                }
                self.setState({ sessionChecked: true });
            })
            .catch(function() {
                self.setState({ sessionChecked: true });
            });
    }
    
    loadUserVotes() {
        var self = this;
        var page = 1;
        var reset = false;
        if (arguments && arguments.length > 0 && typeof arguments[0] === 'number') page = arguments[0];
        if (arguments && arguments.length > 1 && arguments[1] === true) reset = true;
        var pageSize = this.state.userVotesPageSize || 100;
        var params = [];
        params.push('page=' + page);
        params.push('page_size=' + pageSize);
        this.setState({ userVotesLoading: true });
        fetch(this.api('/api/my-votes') + '?' + params.join('&'), { 
            credentials: 'include',
            headers: this.getAuthHeaders()
        })
            .then(function(res) { if (!res.ok) throw new Error('Failed'); return res.json(); })
            .then(function(data) {
                var votes = data && data.votes ? data.votes : [];
                var total = data && data.total ? data.total : 0;
                var lookup = {};
                votes.forEach(function(v) { lookup[v.package_name] = v.vote_type; });
                var newList = reset ? votes : (self.state.userVotes || []).concat(votes);
                var hasMore = newList.length < total;
                self.setState({ userVotes: newList, voteLookup: Object.assign({}, self.state.voteLookup || {}, lookup), userVotesPage: page, userVotesHasMore: hasMore, userVotesTotal: total, userVotesLoading: false }, function() {
                    // No per-vote /api/app calls needed anymore because API returns app_name and icon_url
                });
            })
            .catch(function() { self.setState({ userVotesLoading: false }); });
    }

    _onScroll() {
        if (!this.isProfilePage()) return;
        if (!this.state.userVotesHasMore) return;
        if (this.state.userVotesLoading) return;
        try {
            var scrollPos = (window.innerHeight || document.documentElement.clientHeight) + (window.pageYOffset || document.documentElement.scrollTop || 0);
            var height = document.documentElement.scrollHeight || document.body.scrollHeight || 0;
            if (height - scrollPos < 400) {
                // near bottom
                this.loadUserVotes(this.state.userVotesPage + 1, false);
            }
        } catch (e) {}
    }

    
    
    handleCategoryChange(category) {
        var self = this;
        var currentCategories = this.state.selectedCategories.slice();
        
        if (category === 'all') {
            // Clear all categories
            currentCategories = [];
        } else {
            // Toggle category
            var index = currentCategories.indexOf(category);
            if (index === -1) {
                currentCategories.push(category);
            } else {
                currentCategories.splice(index, 1);
            }
        }
        
        this.setState({ selectedCategories: currentCategories, selectedVendor: '', displayCount: DISPLAY_PAGE_SIZE }, function() {
            var cleared = currentCategories.length === 0;
            var hasSorts = self.state.activeSorts && self.state.activeSorts.length > 0;
            if (cleared && !hasSorts) {
                self.refreshRandomSeed(function() {
                    if (self.apiBase) {
                        self.loadFromAPI(1, true);
                    } else {
                        self.filterAndSortApps();
                    }
                }, true);
            } else {
                if (self.apiBase) {
                    self.loadFromAPI(1, true);
                } else {
                    self.filterAndSortApps();
                }
            }
        });
    }
    
    handleSourceChange(source) {
        var self = this;
        this.setState({ selectedSource: source, displayCount: DISPLAY_PAGE_SIZE }, function() {
            if (self.apiBase) {
                self.loadFromAPI(1, true);
            } else {
                self.filterAndSortApps();
            }
        });
    }
    
    handleVote(app, voteType) {
        var self = this;
        if (!this.state.user) {
            if (this.state.isLegacyBrowser) {
                this.startLegacyLogin();
            } else {
                var loginUrl = this.api('/api/auth/discord/login');
                var sep = loginUrl.indexOf('?') === -1 ? '?' : '&';
                window.location.href = loginUrl + sep + 'return_to=' + encodeURIComponent(window.location.href);
            }
            return;
        }
        var userVote = (this.state.voteLookup && this.state.voteLookup[app.package_name]) || '';
        // If already voted (up or down), block further voting on this app unless deleting
        if (voteType !== 'delete' && userVote) {
            alert('You have already voted for this app.');
            return;
        }
        if (voteType === 'delete') {
            var confirmed = window.confirm('Remove your vote and comment for this app?');
            if (!confirmed) return;
            var payload = { package_name: app.package_name, vote_type: 'delete' };
            fetch(this.api('/api/vote'), {
                method: 'POST',
                credentials: 'include',
                headers: Object.assign({ 'Content-Type': 'application/json' }, this.getAuthHeaders(), (this.state.user && this.state.user.user_id) ? { 'X-User-Id': String(this.state.user.user_id) } : {}),
                body: JSON.stringify(payload)
            })
            .then(function(res) { return res.json(); })
            .then(function(data) {
                if (!data || data.error) {
                    alert(data.error || 'Vote failed');
                    return;
                }
                // Update counts locally
                var apps = self.state.apps.map(function(a) {
                    if (a.package_name === app.package_name && data.counts) {
                        a.voteData = { upvotes: data.counts.upvotes, downvotes: data.counts.downvotes };
                    }
                    return a;
                });
                var filtered = self.state.filteredApps.map(function(a) {
                    if (a.package_name === app.package_name && data.counts) {
                        a.voteData = { upvotes: data.counts.upvotes, downvotes: data.counts.downvotes };
                    }
                    return a;
                });
                var votes = self.state.userVotes.slice().filter(function(v) { return v.package_name !== app.package_name; });
                var lookup = {};
                votes.forEach(function(v) { lookup[v.package_name] = v.vote_type; });
                self.setState({
                    apps: apps,
                    filteredApps: filtered,
                    userVotes: votes,
                    voteLookup: lookup
                });
            })
            .catch(function() {
                alert('Vote failed');
            });
            return;
        }
        // For up/down, go to vote page with params
        var target = '/vote?package=' + encodeURIComponent(app.package_name) + '&type=' + encodeURIComponent(voteType);
        window.location.href = target;
    }

    handleVoteFieldChange(field, value) {
        var patch = {};
        patch[field] = value;
        this.setState(patch);
    }

    handleVoteSubmit(event) {
        if (event && event.preventDefault) event.preventDefault();
        var self = this;
        var params = this.parseQuery();
        var pkg = params.package || params.pkg || '';
        if (!pkg) {
            alert('Missing app');
            return;
        }
        var voteType = this.state.voteFormType || 'up';
        var payload = {
            package_name: pkg,
            vote_type: voteType
        };
        var cleanedComment = this.sanitizeComment(this.state.voteFormComment);
        if (voteType === 'up') {
            if (cleanedComment) {
                payload.comment = cleanedComment;
            }
        } else {
            payload.installed = !!this.state.voteFormInstalled;
            payload.crashed = !!this.state.voteFormCrashed;
            if (!cleanedComment) {
                alert('Comment is required for downvote.');
                return;
            }
            payload.comment = cleanedComment;
        }
        fetch(this.api('/api/vote'), {
            method: 'POST',
            credentials: 'include',
            headers: Object.assign({ 'Content-Type': 'application/json' }, this.getAuthHeaders(), (this.state.user && this.state.user.user_id) ? { 'X-User-Id': String(this.state.user.user_id) } : {}),
            body: JSON.stringify(payload)
        })
        .then(function(res) { return res.json(); })
        .then(function(data) {
            if (!data || data.error) {
                alert(data && data.error ? data.error : 'Vote failed');
                return;
            }
            // Update counts locally
            var apps = self.state.apps.map(function(a) {
                if (a.package_name === pkg && data.counts) {
                    a.voteData = { upvotes: data.counts.upvotes, downvotes: data.counts.downvotes };
                }
                return a;
            });
            var filtered = self.state.filteredApps.map(function(a) {
                if (a.package_name === pkg && data.counts) {
                    a.voteData = { upvotes: data.counts.upvotes, downvotes: data.counts.downvotes };
                }
                return a;
            });
            var votes = self.state.userVotes.slice();
            var existing = votes.find(function(v) { return v.package_name === pkg; });
            if (existing) {
                existing.vote_type = voteType;
                existing.comment = payload.comment || '';
                existing.installed = payload.installed ? 1 : 0;
                existing.crashed = payload.crashed ? 1 : 0;
                existing.updated_at = new Date().toISOString();
            } else {
                votes.unshift({
                    package_name: pkg,
                    vote_type: voteType,
                    comment: payload.comment || '',
                    installed: payload.installed ? 1 : 0,
                    crashed: payload.crashed ? 1 : 0,
                    updated_at: new Date().toISOString()
                });
            }
            var lookup = {};
            votes.forEach(function(v) { lookup[v.package_name] = v.vote_type; });
            self.setState({
                apps: apps,
                filteredApps: filtered,
                userVotes: votes,
                voteLookup: lookup
            }, function() {
                window.location.href = '/';
            });
        })
        .catch(function() {
            alert('Vote failed');
        });
    }
    
    handleVendorClick(vendor) {
        var self = this;
        // Toggle vendor filter - if same vendor clicked, clear it
        var newVendor = this.state.selectedVendor === vendor ? '' : vendor;
        this.setState({ selectedVendor: newVendor, displayCount: DISPLAY_PAGE_SIZE }, function() {
            if (self.apiBase) {
                self.loadFromAPI(1, true);
            } else {
                self.filterAndSortApps();
            }
        });
    }
    
    handleSortChange(sortField) {
        var self = this;
        var currentSorts = this.state.activeSorts.slice();
        
        // Find if this sort field is already active
        var existingIndex = -1;
        for (var i = 0; i < currentSorts.length; i++) {
            if (currentSorts[i].field === sortField) {
                existingIndex = i;
                break;
            }
        }
        
        if (existingIndex === -1) {
            // Add new sort with default direction
            // For name: start with asc (A→Z), for others: start with desc (highest first)
            var defaultDir = (sortField === 'name') ? 'asc' : 'desc';
            currentSorts.push({ field: sortField, direction: defaultDir });
        } else {
            // Cycle through: first click = default, second click = opposite, third click = remove
            var current = currentSorts[existingIndex];
            var defaultDir = (sortField === 'name') ? 'asc' : 'desc';
            var oppositeDir = (defaultDir === 'asc') ? 'desc' : 'asc';
            
            if (current.direction === defaultDir) {
                // Switch to opposite direction
                current.direction = oppositeDir;
            } else {
                // Remove this sort (third click)
                currentSorts.splice(existingIndex, 1);
            }
        }
        
        this.setState({ activeSorts: currentSorts }, function() {
            if (currentSorts.length === 0) {
                self.refreshRandomSeed(function() {
                    if (self.apiBase) {
                        self.loadFromAPI(1, true);
                    } else {
                        self.filterAndSortApps();
                    }
                }, true);
            } else {
                if (self.apiBase) {
                    self.loadFromAPI(1, true);
                } else {
                    self.filterAndSortApps();
                }
            }
        });
    }
    
    loadMore() {
        var currentCount = this.state.displayCount;
        var filteredCount = this.state.filteredApps.length;
        var nextCount = currentCount + DISPLAY_PAGE_SIZE;
        if (nextCount <= filteredCount) {
            this.setState({ displayCount: nextCount });
            return;
        }
        if (this.apiBase && this.state.apiHasMore && !this.state.apiLoading) {
            var nextPage = (this.state.apiPage || 1) + 1;
            this.loadFromAPI(nextPage, false);
        } else {
            this.setState({ displayCount: Math.min(nextCount, filteredCount) });
        }
    }

    sanitizeComment(text) {
        if (!text) return '';
        return String(text).replace(/<[^>]*>/g, '').trim();
    }
    
    filterAndSortApps(serverOrdered) {
        var options = {
            apps: this.state.apps,
            searchQuery: this.state.searchQuery,
            selectedCategories: this.state.selectedCategories,
            selectedSource: this.state.selectedSource,
            selectedVendor: this.state.selectedVendor,
            activeSorts: this.state.activeSorts,
            voteLookup: this.state.voteLookup,
            randomSeed: this.state.randomSeed,
            serverOrdered: !!serverOrdered || !!this.apiBase,
            serverFiltered: !!this.apiBase
        };
        var filtered = filterAndSort(this.state.apps, options);
        this.setState({ filteredApps: filtered });
    }

    openComments(app) {
        var self = this;
        if (!app || !app.package_name) return;
        var pkg = app.package_name;
        this.setState({
            activeCommentPackage: pkg,
            activeCommentApp: app
        }, function() {
            if (typeof document !== 'undefined') {
                self._prevBodyOverflow = document.body.style.overflow || '';
                document.body.style.overflow = 'hidden';
            }
            var existing = self.state.commentData[pkg];
            if (!existing || !existing.items || existing.items.length === 0) {
                self.loadCommentsPage(pkg, 1);
            }
        });
    }

    closeComments() {
        if (typeof document !== 'undefined') {
            document.body.style.overflow = this._prevBodyOverflow || '';
        }
        this.setState({ activeCommentPackage: '', activeCommentApp: null });
    }

    loadCommentsPage(pkg, page) {
        var self = this;
        if (!pkg) return;
        var current = this.state.commentData[pkg];
        if (current && current.loading) return;
        var targetPage = page || ((current && current.page) ? current.page + 1 : 1);
        var pageSize = 20;

        this.setState(function(prev) {
            var map = Object.assign({}, prev.commentData);
            var existing = map[pkg] || { items: [], page: 0, total: 0 };
            map[pkg] = Object.assign({}, existing, { loading: true, error: '' });
            return { commentData: map };
        }, function() {
            fetch(self.api('/api/app?package=' + encodeURIComponent(pkg) + '&page=' + targetPage + '&page_size=' + pageSize), {
                headers: self.getAuthHeaders()
            })
                .then(function(res) { return res.json(); })
                .then(function(data) {
                    var items = (data && data.votes) ? data.votes : [];
                    var total = (data && data.total_comments) ? data.total_comments : 0;
                    self.setState(function(prev) {
                        var map = Object.assign({}, prev.commentData);
                        var existing = map[pkg] || { items: [], page: 0, total: 0 };
                        var merged = (targetPage === 1 ? [] : (existing.items || []).slice());
                        merged = merged.concat(items);
                        merged.sort(function(a, b) {
                            var at = a && a.updated_at ? new Date(a.updated_at).getTime() : 0;
                            var bt = b && b.updated_at ? new Date(b.updated_at).getTime() : 0;
                            return bt - at;
                        });
                        map[pkg] = {
                            items: merged,
                            page: targetPage,
                            total: total,
                            loading: false,
                            error: ''
                        };
                        return { commentData: map };
                    });
                })
                .catch(function() {
                    self.setState(function(prev) {
                        var map = Object.assign({}, prev.commentData);
                        var existing = map[pkg] || {};
                        map[pkg] = Object.assign({}, existing, { loading: false, error: 'Failed to load comments.' });
                        return { commentData: map };
                    });
                });
        });
    }

    formatCommentDate(dateStr) {
        if (!dateStr) return '';
        var date = new Date(dateStr);
        if (isNaN(date.getTime())) return '';
        var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        var day = date.getDate();
        var month = months[date.getMonth()];
        var year = date.getFullYear();
        return day + ' ' + month + ', ' + year;
    }

    renderCommentsPanel() {
        var pkg = this.state.activeCommentPackage;
        if (!pkg) return null;
        var dataMap = this.state.commentData || {};
        var data = dataMap[pkg] || { items: [], page: 0, total: 0, loading: false, error: '' };
        var app = this.state.activeCommentApp || this.getAppMetaForPackage(pkg) || { app_name: pkg };
        var items = data.items || [];
        var displayTotal = data.total || app.comment_count || items.length || 0;
        var badgeText = displayTotal > 20 ? '20+' : String(displayTotal || '0');
        var self = this;

        var rows = items.map(function(item, index) {
            var voteEmoji = item.vote_type === 'down' ? '👎' : '👍';
            var pills = [];
            if (item.vote_type === 'down') {
                if (item.installed) {
                    pills.push(React.createElement('span', { key: 'installed', className: 'bb-comment-pill' }, 'Installed'));
                }
                if (item.crashed) {
                    pills.push(React.createElement('span', { key: 'crashed', className: 'bb-comment-pill danger' }, 'Crashed'));
                }
            }
            var safeText = self.sanitizeComment(item.comment);
            return React.createElement('div', { className: 'bb-comment-row', key: pkg + '-c-' + index },
                React.createElement('div', { className: 'bb-comment-vote' },
                    React.createElement('span', {
                        className: 'bb-comment-chip' + (item.vote_type === 'down' ? ' down' : ' up')
                    }, item.vote_type === 'down' ? 'Downvote' : 'Upvote')
                ),
                React.createElement('div', { className: 'bb-comment-body' },
                    pills.length > 0 ? React.createElement('div', { className: 'bb-comment-pills' }, pills) : null,
                    React.createElement('div', { className: 'bb-comment-text' }, safeText)
                ),
                React.createElement('div', { className: 'bb-comment-date' }, this.formatCommentDate(item.updated_at))
            );
        }.bind(this));

        var bodyContent;
        if (data.loading && items.length === 0) {
            bodyContent = React.createElement('div', { className: 'bb-comments-loading' },
                React.createElement('div', { className: 'bb-spinner bb-spinner-sm' }),
                React.createElement('div', { className: 'bb-comments-loading-text' }, 'Loading comments...')
            );
        } else if (data.error && items.length === 0) {
            bodyContent = React.createElement('div', { className: 'bb-comments-error' },
                React.createElement('p', null, data.error),
                React.createElement('button', {
                    className: 'bb-btn bb-btn-secondary bb-btn-sm',
                    onClick: function() { self.loadCommentsPage(pkg, 1); }
                }, 'Retry')
            );
        } else if (items.length === 0) {
            bodyContent = React.createElement('div', { className: 'bb-comments-empty' }, 'No comments yet.');
        } else {
            bodyContent = rows;
        }

        var hasMore = (data.total && items.length < data.total);

        return React.createElement('div', { className: 'bb-comments-overlay', onClick: this.closeComments },
            React.createElement('div', { className: 'bb-comments-panel', onClick: function(e) { e.stopPropagation(); } },
                React.createElement('div', { className: 'bb-comments-header' },
                    React.createElement('button', {
                        className: 'bb-btn bb-btn-link bb-comments-back',
                        onClick: this.closeComments
                    }, '← Back'),
                    React.createElement('div', { className: 'bb-comments-title' },
                        React.createElement('div', { className: 'bb-comments-app-name' }, app.app_name || pkg),
                        React.createElement('span', { className: 'bb-comments-count' }, '💬 ' + badgeText)
                    )
                ),
                React.createElement('div', { className: 'bb-comments-body' },
                    bodyContent,
                    data.error && items.length > 0 ? React.createElement('div', { className: 'bb-comments-error-inline' }, data.error) : null,
                    hasMore ? React.createElement('div', { className: 'bb-comments-load-more' },
                        React.createElement('button', {
                            className: 'bb-btn bb-btn-secondary bb-btn-sm',
                            onClick: function() { self.loadCommentsPage(pkg, data.page ? data.page + 1 : 1); },
                            disabled: data.loading
                        }, data.loading ? 'Loading…' : 'Load older')
                    ) : null
                )
            )
        );
    }
    
    render() {
        var self = this;
        var state = this.state;
        var displayedApps = this.apiBase ? (state.filteredApps || []) : (state.filteredApps || []).slice(0, state.displayCount);
        var hasMore = this.apiBase ? state.apiHasMore : (state.displayCount < (state.filteredApps ? state.filteredApps.length : 0));
        var rawPath = (typeof window !== 'undefined') ? (window.location.pathname || '/') : '/';
        var path = rawPath.replace(/\/+$/,'');
        if (path === '') path = '/';
        var onProfilePage = path === '/profile';
        var onVotePage = path === '/vote';
        var onAdminPage = path === '/admin';
        var voteParams = this.parseQuery();
        var votePackage = voteParams.package || voteParams.pkg || '';
        var voteApp = null;
        if (votePackage) {
            voteApp = (this.state.apps || []).find(function(a) { return a.package_name === votePackage; }) || null;
        }
        if (!voteApp) voteApp = this.state.votePageApp || null;
        var canAdmin = state.user && state.user.role && state.user.role !== 'user';
        var isLegacy = state.isLegacyBrowser;

        var knownPath = (path === '/' || onProfilePage || onVotePage || onAdminPage);
        if (!knownPath) {
            if (typeof window !== 'undefined') {
                window.location.href = '/';
            }
            return null;
        }

        if (!state.user && path !== '/') {
            if (!state.sessionChecked) {
                return null;
            }
            if (typeof window !== 'undefined') {
                window.location.href = '/';
            }
            return null;
        }

        if (onAdminPage && (!state.user || !canAdmin)) {
            if (typeof window !== 'undefined') {
                window.location.href = '/';
            }
            return null;
        }

        var hasActiveFilters = !!(state.searchQuery ||
            (state.selectedCategories && state.selectedCategories.length > 0) ||
            (state.selectedSource && state.selectedSource !== 'all') ||
            state.selectedVendor);
        var displayedApps = this.apiBase ? (state.filteredApps || []) : (state.filteredApps || []).slice(0, state.displayCount);
        var hasMore = this.apiBase ? state.apiHasMore : (state.displayCount < (state.filteredApps ? state.filteredApps.length : 0));
        var headerTotal = 0;
        if (this.apiBase) {
            if (state.apiLoading) {
                headerTotal = state.totalApps || 0;
            } else {
                headerTotal = state.totalApps || (state.filteredApps ? state.filteredApps.length : 0);
            }
        } else {
            var baseCount = state.apps ? state.apps.length : 0;
            var filteredCount = state.filteredApps ? state.filteredApps.length : 0;
            headerTotal = hasActiveFilters ? filteredCount : baseCount;
        }

        return React.createElement('div', { className: 'bb-app' },
            React.createElement(Header, {
                searchQuery: state.searchQuery,
                onSearch: this.handleSearch,
                categories: state.categories,
                sources: state.sources,
                selectedCategories: state.selectedCategories,
                selectedSource: state.selectedSource,
                selectedVendor: state.selectedVendor,
                activeSorts: state.activeSorts,
                totalApps: onProfilePage ? 0 : headerTotal,
                onCategoryChange: this.handleCategoryChange,
                onSourceChange: this.handleSourceChange,
                onSortChange: this.handleSortChange,
                user: state.user,
                onLogin: function() { 
                    if (this.state.isLegacyBrowser) {
                        this.startLegacyLogin();
                    } else {
                        var loginUrl = this.api('/api/auth/discord/login');
                        var sep = loginUrl.indexOf('?') === -1 ? '?' : '&';
                        window.location.href = loginUrl + sep + 'return_to=' + encodeURIComponent(window.location.href);
                    }
                }.bind(this),
                onLogout: this.handleLogout,
                onProfile: this.goToProfile,
                hideFilters: onProfilePage || onVotePage || onAdminPage || (state.legacyLoginActive && !state.user)
            }),
            onVotePage && React.createElement(VotePage, {
                user: state.user,
                app: voteApp,
                packageName: votePackage,
                voteFormType: state.voteFormType,
                voteFormInstalled: state.voteFormInstalled,
                voteFormCrashed: state.voteFormCrashed,
                voteFormComment: state.voteFormComment,
                onFieldChange: this.handleVoteFieldChange,
                onSubmit: this.handleVoteSubmit,
                onCancel: function(){ window.location.href = '/'; }
            }),
            !state.user && state.isLegacyBrowser && state.legacyCode && React.createElement(LegacyLoginPanel, {
                code: state.legacyCode,
                qrData: state.legacyQrData
            }),
            state.user && onProfilePage && React.createElement(ProfilePage, {
                user: state.user,
                votes: state.userVotes,
                profileTab: state.profileTab,
                onLogout: this.handleLogout,
                onTabChange: function(tab) { self.setState({ profileTab: tab }); },
                onDelete: function(pkg) { self.handleVote({ package_name: pkg }, 'delete'); },
                getAppMeta: function(pkg) { return self.getAppMetaForPackage(pkg); },
                onAdminToggle: canAdmin ? this.goToAdmin : null,
                isAdminView: false,
                isLegacy: isLegacy,
                canAdmin: canAdmin
            }),
            state.user && onAdminPage && React.createElement(AdminPage, {
                user: state.user,
                onLogout: this.handleLogout,
                onBack: this.goToProfile,
                api: this.api.bind(this),
                getAuthHeaders: this.getAuthHeaders,
                isLegacy: isLegacy,
                randomSeed: state.randomSeed
            }),
            state.selectedVendor && !onAdminPage && React.createElement('div', { className: 'container py-3' },
                React.createElement('div', { className: 'bb-active-filter' },
                    React.createElement('span', null, 'Filtering by author: '),
                    React.createElement('strong', null, state.selectedVendor),
                    React.createElement('button', {
                        className: 'bb-clear-filter',
                        onClick: this.handleVendorClick.bind(this, state.selectedVendor)
                    }, '✕')
                )
            ),
            !onProfilePage && !onVotePage && !onAdminPage && !(state.legacyLoginActive && !state.user) && React.createElement('main', { className: 'container bb-main-grid py-2' },
                React.createElement(AppGrid, {
                    apps: displayedApps,
                    isLoading: (state.isLoading || state.apiLoading),
                    error: state.error,
                    hasMore: hasMore,
                    onLoadMore: this.loadMore,
                    totalApps: state.totalApps,
                    onVendorClick: this.handleVendorClick,
                    onCategoryClick: this.handleCategoryChange,
                    onVote: this.handleVote.bind(this),
                    voteLookup: state.voteLookup,
                    user: state.user,
                    onOpenComments: this.openComments,
                    commentData: state.commentData
                })
            ),
            !onProfilePage && !onVotePage && !onAdminPage && (state.legacyLoginActive && !state.user) && React.createElement('div', { className: 'container py-2 bb-legacy-login-block' },
                React.createElement('p', { className: 'bb-legacy-login-note' }, 'Complete login to continue.')
            ),
            this.renderCommentsPanel(),
            React.createElement(Footer, null)
        );
    }
}

export default App;
